<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Note Templates</a></li>
                    </ul>

                    <h3>Manage Note Templates</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewNoteTemplate" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Note Template</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="noteTemplates"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['subject', 'description']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Subject or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No note templates found.
                        </template>

                        <template #loading>
                            Loading note templates. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Type</span>
                                    <span v-if="data.type === 'P'"
                                          :class="'type-badge status-' + data.type">Personal</span>
                                    <span v-if="data.type === 'C'"
                                          :class="'type-badge status-' + data.type">Clinical</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'format'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Format</span>
                                    <span v-if="data.format === 'R'"
                                          :class="'format-badge status-' + data.format">Rich Text</span>
                                    <span v-if="data.format === 'F'"
                                          :class="'format-badge status-' + data.format">Freeform</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Note Template {{ this.noteTemplate.subject }}</h3>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="noteTemplate.type" :options="noteTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="subject">Subject</label>
                        <InputText id="subject" v-model.trim="noteTemplate.subject" required="true"/>
                    </div>

                    <div v-show="noteTemplate.format === 'R'" class="field">
                        <label for="description">Description</label>
                        <Editor v-model="noteTemplate.description" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                                    <span class="ql-formats">
                                                      <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                                      <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                                      <button class="ql-underline"
                                                              v-tooltip.bottom="'Underline'"></button>
                                                      <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                                      <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                                      <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                                      <button class="ql-list" value="bullet"
                                                              v-tooltip.bottom="'Bulleted List'"></button>
                                                      <button class="ql-list" value="ordered"
                                                              v-tooltip.bottom="'Ordered List'"></button>
                                                      <button class="ql-indent" value="-1"
                                                              v-tooltip.bottom="'Indent -1'"></button>
                                                      <button class="ql-indent" value="+1"
                                                              v-tooltip.bottom="'Indent +1'"></button>
                                                      <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                                      <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                                      <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                                    </span>

                            </template>
                        </Editor>
                    </div>

                    <div v-show="noteTemplate.format === 'F'" class="card p-fluid">
                        <label for="description">Description</label>

                        <div class="card p-fluid">

                            <canvas
                                ref="updateCanvas"
                                :width="width"
                                :height="height"
                                style="border: 1px solid #000; touch-action: none;"
                                @mousedown="startDrawingUpdateCanvas"
                                @mousemove="drawUpdateCanvas"
                                @mouseup="stopDrawing"
                                @mouseleave="stopDrawing"
                                @touchstart="startDrawingUpdateCanvas"
                                @touchmove="drawUpdateCanvas"
                                @touchend="stopDrawing"
                            ></canvas>
                        </div>
                        <button @click="clearCanvas">Clear</button>
                        <button @click="saveCanvasUpdateCanvas">Update</button>

                        <!--                        <div class="card p-fluid">-->
                        <!--                            <vueSignature-->
                        <!--                                ref="signature"-->
                        <!--                                class="canvas-container"-->
                        <!--                                :sigOption="option"-->
                        <!--                                :w="'800px'"-->
                        <!--                                :h="'1500px'"-->
                        <!--                                :disabled="disabled"-->
                        <!--                            ></vueSignature>-->
                        <!--                        </div>-->
                        <!--                        <button @click="save1">Save</button>-->
                        <!--                        <button @click="clear1">Clear</button>-->
                        <!--                        <button @click="undo1">Undo</button>-->

                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="noteTemplate.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>


                    <Button label="Update" class="mr-2 mb-2" @click="updateNoteTemplate"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Note Template {{ this.noteTemplate.subject }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Note Template?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteNoteTemplate"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Note Template</h3>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newNoteTemplate.type" :options="noteTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="format">Format</label>
                        <Dropdown v-model="newNoteTemplate.format" :options="formatTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Format"/>
                    </div>

                    <div class="field">
                        <label for="subject">Subject</label>
                        <InputText id="subject" v-model.trim="newNoteTemplate.subject" required="true"/>
                    </div>

                    <div v-show="newNoteTemplate.format === 'R'" class="field">
                        <label for="description">Description</label>
                        <Editor v-model="newNoteTemplate.description" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                                    <span class="ql-formats">
                                                      <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                                      <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                                      <button class="ql-underline"
                                                              v-tooltip.bottom="'Underline'"></button>
                                                      <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                                      <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                                      <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                                      <button class="ql-list" value="bullet"
                                                              v-tooltip.bottom="'Bulleted List'"></button>
                                                      <button class="ql-list" value="ordered"
                                                              v-tooltip.bottom="'Ordered List'"></button>
                                                      <button class="ql-indent" value="-1"
                                                              v-tooltip.bottom="'Indent -1'"></button>
                                                      <button class="ql-indent" value="+1"
                                                              v-tooltip.bottom="'Indent +1'"></button>
                                                      <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                                      <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                                      <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                                    </span>

                            </template>
                        </Editor>
                    </div>

                    <div v-show="newNoteTemplate.format === 'F'" class="card p-fluid">
                        <label for="description">Description</label>

                        <div class="card p-fluid">

                            <canvas
                                ref="canvas"
                                :width="width"
                                :height="height"
                                style="border: 1px solid #000; touch-action: none;"
                                @mousedown="startDrawingNewCanvas"
                                @mousemove="drawNewCanvas"
                                @mouseup="stopDrawing"
                                @mouseleave="stopDrawing"
                                @touchstart="startDrawingNewCanvas"
                                @touchmove="drawNewCanvas"
                                @touchend="stopDrawing"
                            ></canvas>
                        </div>
                        <button @click="clearCanvas">Clear</button>
                        <button @click="saveCanvasNewCanvas">Save</button>

                        <!--                            <vueSignature-->
                        <!--                                ref="signature"-->
                        <!--                                class="canvas-container"-->
                        <!--                                :sigOption="option"-->
                        <!--                                :w="'800px'"-->
                        <!--                                :h="'1500px'"-->
                        <!--                                :disabled="disabled"-->
                        <!--                            ></vueSignature>-->
                        <!--                        <button @click="save">Save</button>-->
                        <!--                        <button @click="clear">Clear</button>-->
                        <!--                        <button @click="undo">Undo</button>-->

                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="newNoteTemplate.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createNoteTemplate"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import NoteTemplateService from "@/service/NoteTemplateService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";
// import VueSignature from "vue-signature";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
        // VueSignature
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'subject', header: 'Subject', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'format', header: 'Format', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'subject', header: 'Subject', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'format', header: 'Format', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            noteTemplate: {},
            noteTemplates: null,
            newNoteTemplate: {},
            originalNoteTemplates: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            noteTypes: [
                {name: 'Personal', code: 'P'},
                {name: 'Clinical', code: 'C'},
            ],
            formatTypes: [
                {name: 'Rich Text', code: 'R'},
                {name: 'Freeform', code: 'F'},
            ],
            isDrawing: false,
            ctx: null, // Canvas rendering context
            width: 800, // Canvas width
            height: 1500, // Canvas height
            // disabled: false,
            // signature: null,
            // option: {
            //     penColor: "rgb(100, 0, 0)",
            //     backgroundColor: "rgb(255,255,255)",
            // },
        }
    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData, noteTemplateData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    NoteTemplateService.getCompanyNoteTemplates(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.noteTemplates = noteTemplateData.data;
                this.originalNoteTemplates = [...this.noteTemplates];

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.noteTemplates = ", this.noteTemplates);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        startDrawingNewCanvas(event) {
            this.isDrawing = true;

            const rect = this.$refs.canvas.getBoundingClientRect();
            const scaleX = this.width / rect.width;
            const scaleY = this.height / rect.height;

            // Calculate the starting position
            const x = (event.clientX - rect.left) * scaleX;
            const y = (event.clientY - rect.top) * scaleY;

            // Initialize lastX and lastY only when starting a new drawing session
            if (this.lastX === null || this.lastY === null) {
                this.lastX = x;
                this.lastY = y;

                // Begin the path for a new stroke
                this.ctx.beginPath();
                this.ctx.moveTo(this.lastX, this.lastY);
            }
        },
        startDrawingUpdateCanvas(event) {
            this.isDrawing = true;

            const rect = this.$refs.updateCanvas.getBoundingClientRect();
            const scaleX = this.width / rect.width;
            const scaleY = this.height / rect.height;

            // Calculate the starting position
            const x = (event.clientX - rect.left) * scaleX;
            const y = (event.clientY - rect.top) * scaleY;

            // Initialize lastX and lastY only when starting a new drawing session
            if (this.lastX === null || this.lastY === null) {
                this.lastX = x;
                this.lastY = y;

                // Begin the path for a new stroke
                this.ctx.beginPath();
                this.ctx.moveTo(this.lastX, this.lastY);
            }
        },
        // draw(event) {
        //     if (!this.isDrawing) return;
        //     const {x, y} = this.getEventPosition(event);
        //     this.ctx.lineTo(x, y);
        //     this.ctx.stroke();
        // },
        // draw(event) {
        //     if (!this.isDrawing) return;
        //
        //     const rect = this.$refs.updateCanvas.getBoundingClientRect();
        //     const scaleX = this.width / rect.width; // Adjust for horizontal scaling
        //     const scaleY = this.height / rect.height; // Adjust for vertical scaling
        //
        //     const x = (event.clientX - rect.left) * scaleX;
        //     const y = (event.clientY - rect.top) * scaleY;
        //
        //     this.ctx.lineTo(x, y);
        //     this.ctx.stroke();
        // },
        // draw(event) {
        //     if (!this.isDrawing) return;
        //
        //     const rect = this.$refs.updateCanvas.getBoundingClientRect();
        //     const scaleX = this.width / rect.width; // Adjust for horizontal scaling
        //     const scaleY = this.height / rect.height; // Adjust for vertical scaling
        //
        //     const x = (event.clientX - rect.left) * scaleX;
        //     const y = (event.clientY - rect.top) * scaleY;
        //
        //     // Move to the last known position if it's the start of a new segment
        //     if (this.lastX === null || this.lastY === null) {
        //         this.ctx.moveTo(x, y);
        //     } else {
        //         this.ctx.moveTo(this.lastX, this.lastY); // Continue from the last position
        //     }
        //
        //     // Draw to the current position
        //     this.ctx.lineTo(x, y);
        //     this.ctx.stroke();
        //
        //     // Update the last known position
        //     this.lastX = x;
        //     this.lastY = y;
        // },
        drawNewCanvas(event) {
            if (!this.isDrawing) return;

            const rect = this.$refs.canvas.getBoundingClientRect();
            const scaleX = this.width / rect.width; // Adjust for horizontal scaling
            const scaleY = this.height / rect.height; // Adjust for vertical scaling

            const x = (event.clientX - rect.left) * scaleX;
            const y = (event.clientY - rect.top) * scaleY;

            // Continue drawing from the last position
            this.ctx.lineTo(x, y);
            this.ctx.stroke();

            // Update the last known position
            this.lastX = x;
            this.lastY = y;
        },
        drawUpdateCanvas(event) {
            if (!this.isDrawing) return;

            const rect = this.$refs.updateCanvas.getBoundingClientRect();
            const scaleX = this.width / rect.width; // Adjust for horizontal scaling
            const scaleY = this.height / rect.height; // Adjust for vertical scaling

            const x = (event.clientX - rect.left) * scaleX;
            const y = (event.clientY - rect.top) * scaleY;

            // Continue drawing from the last position
            this.ctx.lineTo(x, y);
            this.ctx.stroke();

            // Update the last known position
            this.lastX = x;
            this.lastY = y;
        },
        // stopDrawing() {
        //     if (this.isDrawing) {
        //         this.ctx.closePath();
        //         this.isDrawing = false;
        //     }
        // },
        stopDrawing() {
            this.isDrawing = false;

            // Reset the last position so it starts fresh for the next drawing session
            this.lastX = null;
            this.lastY = null;
        },
        clearCanvas() {
            this.ctx.clearRect(0, 0, this.width, this.height);
        },
        saveCanvasNewCanvas() {
            const canvas = this.$refs.canvas;
            const imageData = canvas.toDataURL("image/png");
            if (this.newNoteTemplate) {
                this.newNoteTemplate.description = imageData;
                // console.log("Saved Image Data URL:", imageData);
                // console.log("this.newNoteTemplate.description = ", this.newNoteTemplate.description);
            }
            // You can send this data URL to your server or use it further
        },
        saveCanvasUpdateCanvas() {
            const canvas = this.$refs.updateCanvas;
            const imageData = canvas.toDataURL("image/png");
            if (this.noteTemplate) {
                this.noteTemplate.description = imageData;
                // console.log("Saved Image Data URL:", imageData);
                // console.log("this.noteTemplate.description = ", this.noteTemplate.description);
            }
        },
        // getEventPosition(event) {
        //     const rect = this.$refs.canvas.getBoundingClientRect();
        //     const isTouchEvent = event.type.startsWith("touch");
        //     const clientX = isTouchEvent ? event.touches[0].clientX : event.clientX;
        //     const clientY = isTouchEvent ? event.touches[0].clientY : event.clientY;
        //     return {
        //         x: clientX - rect.left,
        //         y: clientY - rect.top,
        //     };
        // },
        // loadSavedData(base64Data) {
        //     // const canvas = this.$refs.canvas;
        //     const img = new Image();
        //     img.onload = () => {
        //         this.ctx.clearRect(0, 0, this.width, this.height); // Clear the canvas first
        //         this.ctx.drawImage(img, 0, 0, this.width, this.height); // Draw the image on the canvas
        //     };
        //     img.src = base64Data; // Set the source of the image to the saved data URL
        // },
        // save() {
        //     const signatureData = this.$refs.signature.save();
        //     if (this.noteTemplate) {
        //         this.noteTemplate.description = signatureData;
        //         console.log("Saved signature: ", signatureData);
        //     }
        // },
        // clear() {
        //     this.$refs.signature.clear();
        //     if (this.noteTemplate) {
        //         this.noteTemplate.description = '';
        //         console.log("Cleared signature");
        //     }
        // },
        // undo() {
        //     this.$refs.signature.undo();
        //     console.log("Undo action performed");
        // },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callNewNoteTemplate() {
            this.activeTab = 4;
            this.newNoteTemplate = {};
            this.newNoteTemplate.type = 'P';
            this.newNoteTemplate.format = 'R';
            this.newNoteTemplate.status = 'A';

            // const canvas = this.$refs.newCanvas;
            // this.ctx = canvas.getContext("2d");
            // this.ctx.lineWidth = 2; // Set the pen width
            // this.ctx.lineCap = "round"; // Smooth line ends
            // this.ctx.strokeStyle = "black"; // Pen color
            // this.ctx.clearRect(0, 0, this.width, this.height); // Clear canvas

            const canvas = this.$refs.canvas;
            this.ctx = canvas.getContext("2d");
            this.ctx.lineWidth = 2; // Set the pen width
            this.ctx.lineCap = "round"; // Smooth line ends
            this.ctx.strokeStyle = "black"; // Pen color
        },
        callUpdateNoteTemplate(data) {
            this.activeTab = 2;
            this.noteTemplate = data;

            // const canvas = this.$refs.updateCanvas;
            // this.ctx = canvas.getContext("2d");
            // this.ctx.lineWidth = 2; // Set the pen width
            // this.ctx.lineCap = "round"; // Smooth line ends
            // this.ctx.strokeStyle = "black"; // Pen color
            //
            // // Load the existing image
            // const img = new Image();
            // img.onload = () => {
            //     this.ctx.clearRect(0, 0, this.width, this.height); // Clear canvas
            //     this.ctx.drawImage(img, 0, 0, this.width, this.height); // Draw the image
            // };
            // img.src = this.noteTemplate.description; // Set the image source


            const canvas = this.$refs.updateCanvas;

            // Ensure canvas dimensions match your intended resolution
            canvas.width = this.width;
            canvas.height = this.height;

            const ctx = canvas.getContext("2d");

            // Adjust for device pixel ratio
            const dpr = window.devicePixelRatio || 1;
            canvas.width = this.width * dpr;
            canvas.height = this.height * dpr;
            ctx.scale(dpr, dpr);

            // Set drawing properties
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.strokeStyle = "black";

            // Load the Base64 image
            const img = new Image();
            img.onload = () => {
                this.width = img.width;
                this.height = img.height;
                canvas.width = img.width;
                canvas.height = img.height;

                ctx.clearRect(0, 0, this.width, this.height); // Clear the canvas
                ctx.drawImage(img, 0, 0, this.width, this.height); // Draw the image
            };

            img.src = this.noteTemplate.description;

            this.ctx = ctx; // Store the context for drawing

            // this.activeTab = 2;
            // this.noteTemplate = data;
            //
            // const canvas = this.$refs.canvas;
            // this.ctx = canvas.getContext("2d");
            // this.ctx.lineWidth = 2; // Set the pen width
            // this.ctx.lineCap = "round"; // Smooth line ends
            // this.ctx.strokeStyle = "black"; // Pen color
            //
            // const img = new Image();
            // img.onload = () => {
            //     this.ctx.clearRect(0, 0, this.width, this.height); // Clear the canvas first
            //     this.ctx.drawImage(img, 0, 0, this.width, this.height); // Draw the image on the canvas
            // };
            // img.src = this.noteTemplate.description;
            //
            // console.log('[ConfigurationNoteTemplates][callUpdateNoteTemplate] this.noteTemplate.description = ', this.noteTemplate.description);
            // console.log('[ConfigurationNoteTemplates][callUpdateNoteTemplate] img.src = ', img.src);

        },
        callDeleteNoteTemplate(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.noteTemplate = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.noteTemplates !== null) {

                if (isActive) {
                    this.noteTemplates = this.originalNoteTemplates.filter(noteTemplate => noteTemplate.status === 'A');
                } else if (isInactive) {
                    this.noteTemplates = this.originalNoteTemplates.filter(noteTemplate => noteTemplate.status === 'I');
                } else {
                    this.noteTemplates = this.originalNoteTemplates;
                }

            } else {
                // Handle the case when auditTrails is null
                this.noteTemplates = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateNoteTemplate(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteNoteTemplate(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async updateNoteTemplate() {
            this.submitted = true;

            if (this.noteTemplate.id) {

                try {
                    var newNoteTemplateDto = {
                        subject: this.noteTemplate.subject,
                        description: this.noteTemplate.description,
                        type: this.noteTemplate.type,
                        status: this.noteTemplate.status
                    };

                    const response = await NoteTemplateService.updateNoteTemplate(this.noteTemplate.id, newNoteTemplateDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const noteTemplateData = await NoteTemplateService.getCompanyNoteTemplates(this.profile.companyId);
                    this.noteTemplates = noteTemplateData.data;
                    this.originalNoteTemplates = noteTemplateData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated note template',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update note template. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteNoteTemplate() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.noteTemplate.id) {

                    try {
                        const response = await NoteTemplateService.deleteNoteTemplate(this.noteTemplate.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const noteTemplateData = await NoteTemplateService.getCompanyNoteTemplates(this.profile.companyId);
                        this.noteTemplates = noteTemplateData.data;
                        this.originalNoteTemplates = noteTemplateData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted note template',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete note template. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createNoteTemplate() {
            this.submitted = true;

            if (this.newNoteTemplate.subject) {

                console.log('[ConfigurationNoteTemplate][createNoteTemplate] this.newNoteTemplate.description = ', this.newNoteTemplate.description);

                try {
                    var newNoteTemplateDto = {
                        subject: this.newNoteTemplate.subject,
                        description: this.newNoteTemplate.description,
                        type: this.newNoteTemplate.type,
                        format: this.newNoteTemplate.format
                    };

                    const response = await NoteTemplateService.createNoteTemplate(this.profile.companyId, newNoteTemplateDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const noteTemplateData = await NoteTemplateService.getCompanyNoteTemplates(this.profile.companyId);
                    this.noteTemplates = noteTemplateData.data;
                    this.originalNoteTemplates = noteTemplateData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created note template',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create note template. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge, .format-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.type-badge {
    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-N {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-S {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-C {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-U {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-O {
        background: #ECCFFF;
        color: #c75226;
    }

    &.status-D {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-E {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-F {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-G {
        background: #ECCFFF;
        color: #c75226;
    }

    &.status-H {
        background: #C8E6C9;
        color: #256029;
    }

}

.format-badge {
    &.status-R {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-F {
        background: #FFCDD2;
        color: #C63737;
    }
}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

//.canvas-container {
//    border: 1px solid #000;
//    width: 800px;
//    height: 1500px; /* Ensure this is set */
//    position: relative;
//}

canvas {
    background-color: #fff;
    border-radius: 4px;
}

//button {
//    margin-top: 10px;
//    margin-right: 10px;
//    padding: 8px 12px;
//    background-color: #007bff;
//    color: #fff;
//    border: none;
//    border-radius: 4px;
//    cursor: pointer;
//}
//button:hover {
//    background-color: #0056b3;
//}

</style>
